import { createContext, useCallback, useContext, useRef, useState, forwardRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Grow from '@mui/material/Grow'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import type { TransitionProps } from '@mui/material/transitions'

/**
 * App-wide replacement for `window.confirm` — same imperative feel (`await confirm({...})` →
 * boolean), but a themed, animated MUI dialog. Mount <ConfirmProvider> once inside a ThemeProvider;
 * call `useConfirm()` anywhere below it.
 */
export type ConfirmOptions = {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

// Grow from the icon: a touch more life than the default Fade. Springs from center-top.
const GrowTransition = forwardRef(function GrowTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Grow ref={ref} {...props} style={{ transformOrigin: 'center top' }} />
})

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<(v: boolean) => void>()

  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const close = (result: boolean) => {
    resolver.current?.(result)
    resolver.current = undefined
    setOpts(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={opts !== null}
        onClose={() => close(false)}
        TransitionComponent={GrowTransition}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              p: 1,
              borderRadius: '50%',
              bgcolor: 'warning.light',
              color: 'warning.dark',
            }}
          >
            <WarningAmberRoundedIcon />
          </Box>
          {opts?.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{opts?.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => close(false)} variant="outlined" color="inherit">
            {opts?.cancelLabel ?? t('common.no')}
          </Button>
          <Button onClick={() => close(true)} variant="contained" color="primary">
            {opts?.confirmLabel ?? t('common.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext)
  if (!confirm) throw new Error('useConfirm must be used within a ConfirmProvider')
  return confirm
}
